import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { adminApi, adminAuth, type FollowUp } from '../lib/adminApi';

type Scope = 'today' | 'upcoming' | 'completed';

/**
 * Full-page Tasks view. Tabs switch between the three scopes the API
 * supports. Each row links into the parent order so the admin can act.
 */
export function AdminTasks() {
  const navigate = useNavigate();
  const [scope, setScope] = useState<Scope>('today');
  const [items, setItems] = useState<FollowUp[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function refresh(s: Scope) {
    setLoading(true);
    adminApi
      .listFollowUps(s)
      .then((r) => setItems(r.items))
      .catch((e) => setError(String(e.message)))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (!adminAuth.isLoggedIn()) return navigate('/admin/login');
    refresh(scope);
  }, [scope, navigate]);

  async function markDone(id: string) {
    await adminApi.updateFollowUp(id, { status: 'COMPLETED' });
    refresh(scope);
  }

  return (
    <AdminLayout>
      <h1 className="text-2xl font-semibold text-slate-900">Tasks</h1>
      <p className="mt-1 text-sm text-slate-500">
        Follow-ups across all customers. Auto-created on each filing outcome and
        editable per order.
      </p>

      <div className="mt-6 flex gap-1 rounded-lg bg-slate-100 p-1 text-sm w-fit">
        {(['today', 'upcoming', 'completed'] as Scope[]).map((s) => (
          <button
            key={s}
            onClick={() => setScope(s)}
            className={`rounded-md px-3 py-1.5 font-medium capitalize ${
              scope === s
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {error && <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      <div className="mt-4 overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
        {loading && items.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-slate-500">Loading…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-slate-500">
            {scope === 'today' ? 'No tasks for today.' : scope === 'upcoming' ? 'Nothing upcoming.' : 'No completed tasks yet.'}
          </div>
        )}
        {items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Due</th>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-xs">
                    <DateCell iso={t.dueDate} overdue={scope !== 'completed' && t.dueDate < todayIso()} />
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/admin/orders/${t.orderId}`} className="font-medium text-slate-900 hover:text-brand-700">
                      {t.companyName}
                    </Link>
                    <div className="text-xs text-slate-500">{t.companyNumber}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{t.reason}</td>
                  <td className="px-4 py-3 text-xs">
                    {t.autoCreated ? (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">AUTO</span>
                    ) : (
                      <span className="rounded-full bg-brand-50 px-2 py-0.5 text-brand-700 ring-1 ring-brand-200">MANUAL</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {scope !== 'completed' ? (
                      <button
                        onClick={() => markDone(t.id)}
                        className="text-xs text-brand-700 hover:underline"
                      >
                        Mark done
                      </button>
                    ) : t.completedAt ? (
                      <span className="text-xs text-slate-400">
                        ✓ {new Date(t.completedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}

function DateCell({ iso, overdue }: { iso: string; overdue: boolean }) {
  const d = new Date(iso + 'T00:00:00Z');
  return (
    <span className={overdue ? 'font-semibold text-red-600' : 'text-slate-600'}>
      {d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}
    </span>
  );
}
function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
